import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api-client';
import type { ApiValidationIssue } from '@/lib/api-client';
import { WizardContainer } from '@/components/wizard/wizard-container';
import { WizardProgress } from '@/components/wizard/wizard-progress';
import { WizardNavigation } from '@/components/wizard/wizard-navigation';
import { StepRouting } from '@/components/contracts/nda/step-routing';
import { StepParties } from '@/components/contracts/nda/step-parties';
import { StepSubject } from '@/components/contracts/nda/step-subject';
import { StepTerms } from '@/components/contracts/nda/step-terms';
import { StepAdvancedScope } from '@/components/contracts/nda/step-advanced-scope';
import { StepAdvancedDisclosure } from '@/components/contracts/nda/step-advanced-disclosure';
import { StepAdvancedFinal } from '@/components/contracts/nda/step-advanced-final';
import { NdaSummary } from '@/components/contracts/nda/nda-summary';
import { useSession } from '@/lib/auth-client';
import { useContract, useContractTransition, useCreateContract } from '@/hooks/api/use-contracts';
import { useComments } from '@/hooks/api/use-comments';
import { useAutoSave } from '@/hooks/use-auto-save';
import { useWizard } from '@/hooks/use-wizard';
import { useNdaSmartDefaults } from '@/hooks/use-nda-defaults';
import { SummaryReturnBanner } from '@/components/wizard/summary-return-banner';
import { NameContractModal } from '@/components/contracts/name-contract-modal';
import { StepCommentBanner } from '@/components/review/step-comment-banner';
import { getCommentCountsPerStep } from '@/components/review/comment-step-utils';
import type { Warning, Suggestion } from '@/components/wizard/wizard-summary';
import type { NdaWizardForm } from '@/components/contracts/nda/types';

// --- Route definition ---

export const Route = createFileRoute('/_authenticated/contracts/new-nda')({
  component: NdaWizardPage,
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: (search.id as string) || undefined,
  }),
});

// --- Constants ---

const CORE_STEP_COUNT = 5; // Steps 1-4 + Summary (progress bar shows all 5)
const CORE_STEPS = 5; // Steps 1-4 + Summary (internal navigation)
const ADVANCED_STEPS = 3; // Steps 5-7

// --- Page Component ---

function NdaWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { id: initialId } = Route.useSearch();
  const [contractId, setContractId] = useState(initialId ?? '');
  const contractIdRef = useRef(initialId ?? '');
  const createMutation = useCreateContract();

  // Mode: 'core' (steps 1-4 + summary) or 'advanced' (steps 5-7)
  const [mode, setMode] = useState<'core' | 'advanced'>('core');
  const [submitErrors, setSubmitErrors] = useState<ApiValidationIssue[]>([]);
  const [advancedStep, setAdvancedStep] = useState(0); // 0-2 for steps 5-7
  const [advancedDirection, setAdvancedDirection] = useState<'forward' | 'backward'>('forward');
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const defaultsAppliedRef = useRef(false);
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);

  // React Hook Form
  const form = useForm<NdaWizardForm>({
    defaultValues: {
      routing: {},
      parties: { party_a: {}, party_b: {} },
      subject: {},
      terms: { duration: { value: 1, unit: 'months' } },
      advanced: {},
      _meta: { form_version: '1.0', smart_defaults_applied: false, defaults_overridden: [] },
    },
  });

  // Load existing contract data
  const { data: contract, isLoading: isLoadingContract } = useContract(contractId, {
    enabled: !!contractId,
  });

  // Comments for CHANGES_REQUESTED state — comment badges + banners
  const isChangesRequested = contract?.status === 'CHANGES_REQUESTED';
  const { data: commentsData } = useComments(
    contractId,
    {},
    {
      enabled: !!contractId && isChangesRequested,
    },
  );
  const openComments = useMemo(
    () => (commentsData?.comments ?? []).filter((c) => c.status === 'OPEN'),
    [commentsData?.comments],
  );
  const commentCountsPerStep = useMemo(
    () =>
      isChangesRequested && openComments.length > 0
        ? getCommentCountsPerStep(openComments, 'NDA', CORE_STEP_COUNT)
        : [],
    [isChangesRequested, openComments],
  );

  // Populate form from existing contract — only on first load, not after auto-save refetches
  const hasRestoredForm = useRef(false);
  const hasRestoredStep = useRef(false);
  useEffect(() => {
    if (hasRestoredForm.current || !contract?.formData) return;
    const formData = contract.formData as unknown as NdaWizardForm;
    if (formData.routing) {
      form.reset({
        ...formData,
        terms: {
          duration: { value: 1, unit: 'months' },
          ...formData.terms,
        },
      });
      hasRestoredForm.current = true;
      // If defaults were already applied in a previous session, skip re-applying them.
      // Use ref (synchronous) so the smart defaults effect in the same cycle sees it.
      if (formData._meta?.smart_defaults_applied) {
        defaultsAppliedRef.current = true;
        setDefaultsApplied(true);
      }
    }
  }, [contract, form]);

  // Smart defaults
  const ndaType = form.watch('routing.nda_type');
  const context = form.watch('routing.context');
  const perspective = form.watch('routing.perspective');

  const { data: defaultsResponse } = useNdaSmartDefaults({
    ndaType,
    context,
    perspective,
  });

  // Track previous routing to detect changes
  const prevRoutingRef = useRef({ ndaType, context, perspective });

  // Apply smart defaults — reset on routing change, apply when defaults arrive
  useEffect(() => {
    const prev = prevRoutingRef.current;
    const routingChanged =
      prev.ndaType !== ndaType || prev.context !== context || prev.perspective !== perspective;
    prevRoutingRef.current = { ndaType, context, perspective };

    if (routingChanged) {
      defaultsAppliedRef.current = false;
      setDefaultsApplied(false);
    }

    if (defaultsResponse && !defaultsAppliedRef.current && ndaType && context && perspective) {
      const defaults = defaultsResponse.data as Record<string, unknown>;
      if (defaults?.advanced) {
        form.setValue('advanced', defaults.advanced as unknown as NdaWizardForm['advanced'], {
          shouldDirty: false,
        });
      }
      if (defaults?.terms) {
        const termsDefaults = defaults.terms as Record<string, unknown>;
        if (termsDefaults.confidentiality_period) {
          form.setValue(
            'terms.confidentiality_period',
            termsDefaults.confidentiality_period as unknown as NdaWizardForm['terms']['confidentiality_period'],
            { shouldDirty: false },
          );
        }
        if (termsDefaults.liability_mechanism) {
          form.setValue(
            'terms.liability_mechanism',
            termsDefaults.liability_mechanism as unknown as NdaWizardForm['terms']['liability_mechanism'],
            { shouldDirty: false },
          );
        }
      }
      form.setValue('_meta.smart_defaults_applied', true, { shouldDirty: false });
      defaultsAppliedRef.current = true;
      setDefaultsApplied(true);
    }
  }, [defaultsResponse, defaultsApplied, ndaType, context, perspective, form]);

  // Draft edit mode — transactional: no auto-save, only save+regenerate on submit
  const isDraftEdit = contract?.status === 'DRAFT' || contract?.status === 'DRAFT_SELECTION';

  // Auto-save
  const { save, saveImmediate } = useAutoSave({ contractId, enabled: !!contractId });

  // Core wizard
  const wizard = useWizard({
    totalSteps: CORE_STEPS,
    initialStep: 0,
    onStepChange: (_from, _to) => {
      window.scrollTo(0, 0);
      if (_to === CORE_STEPS - 1) setEditingFromSummary(false);
      if (contractId) {
        form.setValue('_meta.current_step', _to, { shouldDirty: false });
        if (!isDraftEdit) {
          save(form.getValues() as unknown as Record<string, unknown>, _to);
        }
      }
    },
    validateStep: async (step) => {
      if (step === 0) {
        // Step 1: require all routing
        if (!(ndaType && context && perspective)) return false;
        // Create contract on first advance past step 1
        if (!contractIdRef.current) {
          try {
            const response = await createMutation.mutateAsync({ contractType: 'NDA' });
            const newId = (response as { data: { id: string } }).data.id;
            contractIdRef.current = newId;
            setContractId(newId);
            void navigate({
              to: '/contracts/new-nda',
              search: { id: newId },
              replace: true,
            });
          } catch {
            toast.error(t('contractsList.createError'));
            return false;
          }
        }
        return true;
      }
      if (step === 1) {
        // Step 2: require legal form selected for both parties
        const partyAForm = form.getValues('parties.party_a.legal_form');
        const partyBForm = form.getValues('parties.party_b.legal_form');
        return !!(partyAForm && partyBForm);
      }
      if (step === 2) {
        // Step 3: require purpose (min 10 chars) + min 1 information category
        const purpose = form.getValues('subject.purpose') ?? '';
        const categories = form.getValues('subject.information_categories');
        return purpose.length >= 10 && Array.isArray(categories) && categories.length > 0;
      }
      if (step === 3) {
        // Step 4: require duration, confidentiality period, liability mechanism
        const dur = form.getValues('terms.duration');
        const conf = form.getValues('terms.confidentiality_period');
        const liability = form.getValues('terms.liability_mechanism');

        const isDurationValid =
          dur?.unit === 'indefinite' || (!!dur?.unit && !!dur?.value && dur.value > 0);
        const isConfValid =
          conf?.unit === 'indefinite' || (!!conf?.unit && !!conf?.value && conf.value > 0);

        if (!isDurationValid || !isConfValid || !liability) return false;

        // Penalty fields required when penalty mechanism selected
        const hasPenalty =
          liability === 'penalty' ||
          liability === 'penalty_plus_supplementary' ||
          liability === 'penalty_plus_full';
        if (hasPenalty) {
          const penaltyAmount = form.getValues('terms.penalty_amount');
          const penaltyType = form.getValues('terms.penalty_type');
          if (!penaltyAmount || penaltyAmount <= 0 || !penaltyType) return false;
        }

        // Cap fields required when cap enabled
        const capEnabled = form.getValues('terms.liability_cap_enabled');
        if (capEnabled) {
          const capAmount = form.getValues('terms.liability_cap_amount');
          const capScope = form.getValues('terms.liability_cap_scope');
          if (!capAmount || capAmount <= 0 || !capScope) return false;
        }

        return true;
      }
      return true;
    },
  });

  // Resume wizard at the saved step (after contract + wizard are ready)
  useEffect(() => {
    if (hasRestoredStep.current || !contract?.formData) return;
    const meta = (contract.formData as Record<string, unknown>)?._meta as
      | Record<string, unknown>
      | undefined;
    const savedStep = meta?.current_step as number | undefined;
    if (typeof savedStep === 'number' && savedStep > 0 && savedStep < CORE_STEPS) {
      wizard.jumpToStep(savedStep);
    }
    hasRestoredStep.current = true;
  }, [contract, wizard]);

  // Transition mutation (submit)
  const transition = useContractTransition(contractId);

  // --- Warnings & suggestions ---
  // 1. Backend context-based warnings (from smart defaults API)
  const warnings: Warning[] = [];
  const suggestions: Suggestion[] = [];

  if (defaultsResponse) {
    const data = defaultsResponse.data as Record<string, unknown>;
    if (Array.isArray(data?.warnings)) {
      for (const w of data.warnings as Array<{ id: string; message: string; tier: number }>) {
        warnings.push({ message: w.message, tier: w.tier as 1 | 2 | 3 });
      }
    }
    if (Array.isArray(data?.suggestions)) {
      for (const s of data.suggestions as Array<{ id: string; message: string }>) {
        suggestions.push({ message: s.message });
      }
    }
  }

  // 2. Form-reactive warnings — based on actual user selections
  const partyALegalForm = form.watch('parties.party_a.legal_form');
  const partyBLegalForm = form.watch('parties.party_b.legal_form');
  const includesPersonalData = form.watch('subject.includes_personal_data');
  const penaltyAmount = form.watch('terms.penalty_amount');
  const cooperationValue = form.watch('terms.cooperation_value');
  const liabilityMechanism = form.watch('terms.liability_mechanism');
  const penaltyType = form.watch('terms.penalty_type');
  const watchedAdvanced = form.watch('advanced');
  const governingLaw = watchedAdvanced?.final_provisions?.governing_law;
  const executionForm = watchedAdvanced?.final_provisions?.execution_form;

  // W1: Spółka cywilna — no legal personality
  if (partyALegalForm === 'spolka_cywilna' || partyBLegalForm === 'spolka_cywilna') {
    warnings.push({ message: t('wizard.warnings.spolkaCywilna'), tier: 1 });
  }

  // W3: Foreign entity — consider governing law + language
  if (partyALegalForm === 'foreign_entity' || partyBLegalForm === 'foreign_entity') {
    warnings.push({ message: t('wizard.warnings.foreignEntity'), tier: 2 });
  }

  // W4: Personal data — GDPR clause, consider DPA
  if (includesPersonalData) {
    warnings.push({ message: t('wizard.warnings.personalData'), tier: 2 });
  }

  // W5: Penalty disproportionate to cooperation value
  if (cooperationValue && penaltyAmount && penaltyAmount > cooperationValue * 2) {
    warnings.push({ message: t('wizard.warnings.penaltyDisproportionate'), tier: 2 });
  }

  // W9: Per-breach penalty — consider proportionality
  if (penaltyType === 'per_breach' && penaltyAmount) {
    warnings.push({ message: t('wizard.warnings.perBreachProportionality'), tier: 3 });
  }

  // W10: Non-Polish governing law
  if (governingLaw && governingLaw !== 'polish') {
    warnings.push({ message: t('wizard.warnings.foreignLaw'), tier: 2 });
  }

  // W14: Electronic execution form
  if (executionForm === 'electronic') {
    warnings.push({ message: t('wizard.warnings.electronicForm'), tier: 2 });
  }

  // No liability mechanism — weak enforcement
  if (liabilityMechanism === 'none') {
    warnings.push({ message: t('wizard.warnings.noLiability'), tier: 2 });
  }

  // Form-reactive suggestions
  if (includesPersonalData) {
    suggestions.push({ message: t('wizard.suggestions.dpa') });
  }
  if (liabilityMechanism === 'general' || liabilityMechanism === 'none') {
    suggestions.push({ message: t('wizard.suggestions.addPenalty') });
  }
  if (partyALegalForm === 'foreign_entity' || partyBLegalForm === 'foreign_entity') {
    suggestions.push({ message: t('wizard.suggestions.bilingual') });
  }

  // Suggested penalty range
  const suggestedPenaltyRange = defaultsResponse
    ? ((defaultsResponse.data as Record<string, unknown>)?.suggestedPenaltyRange as
        | [number, number]
        | undefined)
    : undefined;

  // Submit handler — for new contracts, opens name modal first
  const handleSubmit = useCallback(() => {
    if (!contractId) return;
    if (!isDraftEdit) {
      setNameModalOpen(true);
      return;
    }
    executeGeneration();
  }, [contractId, isDraftEdit]);

  // Actual generation — called directly (regenerate) or after naming (new contract)
  const executeGeneration = useCallback(async () => {
    if (!contractId) return;
    setSubmitErrors([]);

    try {
      // Save final form data — flush immediately, no debounce
      await saveImmediate(
        form.getValues() as unknown as Record<string, unknown>,
        wizard.currentStep,
      );

      // Transition to GENERATING — use 'regenerate' for draft edit, 'submit' for new
      const action = isDraftEdit ? 'regenerate' : 'submit';
      transition.mutate(
        { action },
        {
          onSuccess: (res) => {
            const identifier = (res as { data?: { contractIdentifier?: string } })?.data
              ?.contractIdentifier;
            toast.success(
              identifier
                ? t('wizard.generateSuccess', { identifier })
                : t('wizard.generateSuccessGeneric'),
            );
            navigate({ to: '/contracts', search: { status: undefined } });
          },
          onError: (error) => {
            if (error instanceof ApiError && error.issues?.length) {
              setSubmitErrors(error.issues);
            } else {
              toast.error(t('contractsList.createError'));
            }
          },
        },
      );
    } catch {
      toast.error(t('contractsList.createError'));
    }
  }, [contractId, form, wizard.currentStep, saveImmediate, transition, navigate, t, isDraftEdit]);

  // Name modal confirm — save title then generate
  const handleNameConfirm = useCallback(
    async (title: string) => {
      if (!contractId) return;
      try {
        await api.patch(`/contracts/${contractId}/title`, { title });
      } catch {
        // Non-blocking — title can be changed later
      }
      setNameModalOpen(false);
      executeGeneration();
    },
    [contractId, executeGeneration],
  );

  // Enter advanced mode (always from summary), optionally at a specific step
  const handleCustomize = useCallback((step?: number) => {
    setEditingFromSummary(true);
    setMode('advanced');
    setAdvancedStep(step ?? 0);
    setAdvancedDirection('forward');
    window.scrollTo(0, 0);
  }, []);

  // Advanced navigation
  const handleAdvancedNext = useCallback(() => {
    if (advancedStep < ADVANCED_STEPS - 1) {
      setAdvancedDirection('forward');
      setAdvancedStep((s) => s + 1);
      if (contractId && !isDraftEdit) {
        save(form.getValues() as unknown as Record<string, unknown>, advancedStep + 5);
      }
    } else {
      // Return to summary
      setMode('core');
      if (contractId && !isDraftEdit) {
        save(form.getValues() as unknown as Record<string, unknown>, 4);
      }
    }
  }, [advancedStep, contractId, form, save, isDraftEdit]);

  const handleAdvancedBack = useCallback(() => {
    if (advancedStep > 0) {
      setAdvancedDirection('backward');
      setAdvancedStep((s) => s - 1);
    } else {
      // Return to summary
      setMode('core');
      if (contractId && !isDraftEdit) {
        save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
      }
    }
  }, [advancedStep, contractId, form, save, isDraftEdit]);

  // Step labels for progress bar — always show 4 CORE steps (summary is not a "step")
  const coreStepLabels = [
    t('ndaWizard.step1.title'),
    t('ndaWizard.step2.title'),
    t('ndaWizard.step3.title'),
    t('ndaWizard.step4.title'),
    t('wizard.summary'),
  ];

  const progressSteps = coreStepLabels.map((label, i) => ({
    label,
    hasError: wizard.stepErrors[i],
    hasWarning: wizard.stepWarnings[i],
    commentCount: commentCountsPerStep[i],
  }));

  // Loading state
  if (contractId && isLoadingContract) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Guard: only creator can edit the form
  if (contract && session?.user?.id && contract.createdBy !== session.user.id) {
    void navigate({ to: '/contracts/$contractId', params: { contractId } });
    return null;
  }

  // Advanced mode rendering
  if (mode === 'advanced') {
    const advancedStepLabels = [
      t('ndaWizard.step5.title'),
      t('ndaWizard.step6.title'),
      t('ndaWizard.step7.title'),
    ];

    return (
      <WizardContainer title={t('ndaWizard.title')} subtitle={t('ndaWizard.subtitle')}>
        <div className="flex flex-1 flex-col">
          {/* Advanced progress */}
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <WizardProgress
              steps={advancedStepLabels.map((label) => ({ label }))}
              currentStep={advancedStep}
              totalSteps={ADVANCED_STEPS}
              mode="advanced"
            />
          </div>

          {/* Advanced step content */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
            <SummaryReturnBanner
              onReturn={() => {
                setEditingFromSummary(false);
                setMode('core');
                wizard.jumpToStep(CORE_STEPS - 1);
                if (contractId && !isDraftEdit) {
                  save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
                }
                window.scrollTo(0, 0);
              }}
            />
            {advancedStep === 0 && (
              <StepAdvancedScope
                form={form}
                direction={advancedDirection}
                defaultsApplied={defaultsApplied}
              />
            )}
            {advancedStep === 1 && (
              <StepAdvancedDisclosure
                form={form}
                direction={advancedDirection}
                defaultsApplied={defaultsApplied}
              />
            )}
            {advancedStep === 2 && (
              <StepAdvancedFinal
                form={form}
                direction={advancedDirection}
                defaultsApplied={defaultsApplied}
              />
            )}
          </div>

          {/* Advanced navigation */}
          <WizardNavigation
            onBack={handleAdvancedBack}
            onNext={handleAdvancedNext}
            isFirstStep={false}
            isLastStep={advancedStep === ADVANCED_STEPS - 1}
            submitLabel={t('wizard.summary')}
            onSubmit={advancedStep === ADVANCED_STEPS - 1 ? handleAdvancedNext : undefined}
          />
        </div>
      </WizardContainer>
    );
  }

  // Core mode rendering
  const isSummaryStep = wizard.currentStep === CORE_STEPS - 1;

  return (
    <>
      <WizardContainer title={t('ndaWizard.title')} subtitle={t('ndaWizard.subtitle')}>
        <div className="flex flex-1 flex-col">
          {/* Progress bar — show all 5 core steps including summary */}
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <WizardProgress
              steps={progressSteps}
              currentStep={wizard.currentStep}
              totalSteps={CORE_STEP_COUNT}
            />
          </div>

          {/* Step content */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
            {editingFromSummary && !isSummaryStep && (
              <SummaryReturnBanner
                onReturn={() => {
                  setEditingFromSummary(false);
                  wizard.jumpToStep(CORE_STEPS - 1);
                  if (contractId && !isDraftEdit) {
                    save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
                  }
                  window.scrollTo(0, 0);
                }}
              />
            )}
            {isChangesRequested && !isSummaryStep && openComments.length > 0 && (
              <StepCommentBanner
                comments={openComments}
                contractType="NDA"
                step={wizard.currentStep}
              />
            )}
            {wizard.currentStep === 0 && <StepRouting form={form} direction={wizard.direction} />}
            {wizard.currentStep === 1 && <StepParties form={form} direction={wizard.direction} />}
            {wizard.currentStep === 2 && <StepSubject form={form} direction={wizard.direction} />}
            {wizard.currentStep === 3 && (
              <StepTerms
                form={form}
                direction={wizard.direction}
                suggestedPenaltyRange={suggestedPenaltyRange}
              />
            )}
            {isSummaryStep && (
              <NdaSummary
                form={form}
                warnings={warnings}
                suggestions={suggestions}
                onSubmit={handleSubmit}
                onCustomize={handleCustomize}
                isSubmitting={transition.isPending}
                onGoToStep={(step) => {
                  setEditingFromSummary(true);
                  wizard.jumpToStep(step);
                  window.scrollTo(0, 0);
                }}
                submitLabel={isDraftEdit ? t('wizard.regenerate') : undefined}
                submitErrors={submitErrors}
              />
            )}
          </div>

          {/* Navigation — show on all steps including summary (back button) */}
          {!isSummaryStep && (
            <WizardNavigation
              onBack={wizard.goBack}
              onNext={wizard.goNext}
              isFirstStep={wizard.isFirstStep}
              isLastStep={false}
              nextLabel={wizard.currentStep === 3 ? t('wizard.summary') : undefined}
              canGoNext={
                wizard.currentStep === 0
                  ? !!(ndaType && context && perspective)
                  : wizard.currentStep === 1
                    ? !!(
                        form.watch('parties.party_a.legal_form') &&
                        form.watch('parties.party_b.legal_form')
                      )
                    : wizard.currentStep === 2
                      ? (form.watch('subject.purpose') ?? '').length >= 10 &&
                        (form.watch('subject.information_categories') ?? []).length > 0
                      : wizard.currentStep === 3
                        ? (() => {
                            const dur = form.watch('terms.duration');
                            const conf = form.watch('terms.confidentiality_period');
                            const liability = form.watch('terms.liability_mechanism');
                            const isDurOk =
                              dur?.unit === 'indefinite' ||
                              (!!dur?.unit && !!dur?.value && dur.value > 0);
                            const isConfOk =
                              conf?.unit === 'indefinite' ||
                              (!!conf?.unit && !!conf?.value && conf.value > 0);
                            if (!isDurOk || !isConfOk || !liability) return false;
                            const hasPen =
                              liability === 'penalty' ||
                              liability === 'penalty_plus_supplementary' ||
                              liability === 'penalty_plus_full';
                            if (hasPen) {
                              const amt = form.watch('terms.penalty_amount');
                              const typ = form.watch('terms.penalty_type');
                              if (!amt || amt <= 0 || !typ) return false;
                            }
                            const capOn = form.watch('terms.liability_cap_enabled');
                            if (capOn) {
                              const capAmt = form.watch('terms.liability_cap_amount');
                              const capScope = form.watch('terms.liability_cap_scope');
                              if (!capAmt || capAmt <= 0 || !capScope) return false;
                            }
                            return true;
                          })()
                        : true
              }
            />
          )}
          {isSummaryStep && (
            <WizardNavigation onBack={wizard.goBack} isFirstStep={false} isLastStep={false} />
          )}
        </div>
      </WizardContainer>

      <NameContractModal
        open={nameModalOpen}
        onOpenChange={setNameModalOpen}
        contractType="NDA"
        contractId={contractId ?? ''}
        onConfirm={handleNameConfirm}
        isPending={transition.isPending}
      />
    </>
  );
}
